import { compose, isEmpty, join, map, toPairs } from 'lodash/fp';
import type AdminLink from '../../../../../types/AdminLink';
import type { CurrentUserDto } from '../../../../../types/UserDtos';
import AlertBox from '../../../components/AlertBox';
import PageStaticSection from '../../../components/PageStaticSection';
import { MultipleUiWarning } from '../../../components/UIWarning';
import { get, getWithCount } from '../../../services/Pluralize';
import { enumerateRoles, isSuperUser } from '../../../services/User';
import AdminLinkButton from './AdminLinkButton';
import type { LinkCategories } from './service';
import styles from './style.m.less';
import { useMutedSuggestions } from './useMutedSuggestions';

type AdminOverviewProps = {
    currentUser: CurrentUserDto;
    categories: LinkCategories;
    errorCount: number;
};

export default function AdminOverview({ currentUser, categories: allCategories, errorCount }: AdminOverviewProps) {
    const { categories, suggestions } = useMutedSuggestions(allCategories);

    const hasDisabledLinks = Object.values(categories).some(category => category.some(link => !link.enabled));
    const hasOnlyOneCategory = Object.keys(categories).length === 1;

    return (
        <>
            <PageStaticSection>
                {errorCount > 0 && (
                    <AlertBox colour="RED">
                        <ErrorSummary count={errorCount} />
                    </AlertBox>
                )}

                <MultipleUiWarning warnings={suggestions} colour="BLUE" expirationDays={31} />
                {isSuperUser(currentUser) ||
                    (hasDisabledLinks && (
                        <AlertBox colour="YELLOW">
                            <RoleWarning user={currentUser} />
                        </AlertBox>
                    ))}
            </PageStaticSection>
            {compose(
                map(([title, links]) => (
                    <AdminOverviewCategory
                        links={links}
                        title={title}
                        key={title}
                        currentUser={currentUser}
                        omitSectionHeader={hasOnlyOneCategory}
                    />
                )),
                toPairs
            )(categories)}
        </>
    );
}

type AdminOverviewCategoryProps = {
    title: string;
    links: AdminLink[];
    currentUser: CurrentUserDto;
    omitSectionHeader: boolean;
};

function AdminOverviewCategory({ title, links, currentUser, omitSectionHeader }: AdminOverviewCategoryProps) {
    return (
        <PageStaticSection className={styles.section}>
            {!omitSectionHeader && <h1 className={styles.sectionHeading}>{title}</h1>}
            <div className={styles.gridContainer}>
                {map(
                    link => (
                        <AdminLinkButton
                            {...link}
                            key={link.id}
                            target={link.category === 'Promotions & Reviews' ? '_blank' : ''}
                            currentUser={currentUser}
                        />
                    ),
                    links
                )}
            </div>
        </PageStaticSection>
    );
}

function ErrorSummary({ count }: { count: number }) {
    return (
        <p>
            There {get('is', count, 'are')} {getWithCount('section', count)} with errors on this Administration page.
            Please review all highlighted sections below in order to avoid unexpected issues.
        </p>
    );
}

function RoleWarning({ user }: { user: CurrentUserDto }) {
    const roles = compose(
        join(', '),
        map(r => r.name),
        enumerateRoles
    )(user);
    return (
        <p>
            <strong>Please note:</strong>&nbsp;Due to your user permissions{!isEmpty(roles) && ` (${roles})`} you cannot
            access all options.{' '}
            <a href="https://intercomdocs.small-improvements.com/en/articles/9146183-hr-admins-tech-admins-and-hr-assistants#h_7859d735ca">
                Learn more about SI roles and permissions
            </a>
            .
        </p>
    );
}
